/* ============================================================
   Kit Builder — Smykkebixen
   - Tracks user's selections per step
   - Two kinds of step:
       'slot'  (original) pick exactly one product
       'pool'  (new)      distribute N units across several products
   - Calculates live total = sum( pack_count × variant.price )
   - Max kit_qty = floor( (variant.inventory * pack_size) / units_per_kit )
     across all selected steps (respecting "continue selling when oos")
   - On submit: POST /cart/add.js with all selected variants

   The pool step returns MANY selection objects where a slot step returns
   one. Everything below readSlot() reduces over a flat array of selections,
   so pricing, the inventory ceiling and the cart payload are shared code.
   ============================================================ */
(function () {
  'use strict';

  // Round to 6 decimals to absorb binary float drift (e.g. 0.1 * 30 must be
  // exactly 3, not 3.0000000000000004). Used wherever fractional units-per-kit
  // feed into a Math.ceil / Math.floor — a single overshoot would otherwise
  // charge the customer for an extra pack or hide an available kit.
  function roundq(value) {
    return Math.round(value * 1e6) / 1e6;
  }
  function ceilq(value) {
    return Math.ceil(roundq(value));
  }
  function floorq(value) {
    return Math.floor(roundq(value));
  }

  function formatMoney(cents, format) {
    cents = Math.round(cents);
    const amount = (cents / 100);
    const formatted = amount.toLocaleString('da-DK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    if (format && format.includes('{{amount}}')) {
      return format.replace(/\{\{\s*amount\s*\}\}/g, formatted);
    }
    return formatted + ' kr';
  }

  // Danish plurals for the sales units used in custom.enhed. Unknown units
  // are left alone rather than guessed at, so a new unit can never produce
  // something embarrassing like "rullers".
  const UNIT_PLURAL = {
    'pose': 'poser',
    'pakke': 'pakker',
    'rulle': 'ruller',
    'streng': 'strenge',
    'kort': 'kort',
    'par': 'par',
    'sæt': 'sæt',
    'meter': 'meter',
    'stk.': 'stk.',
    'stk': 'stk.'
  };
  function pluralUnit(unit, count) {
    if (count === 1) return unit;
    return UNIT_PLURAL[unit] || unit;
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  const TRASH_SVG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" ' +
    'aria-hidden="true" focusable="false"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 ' +
    '2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>';

  // ---- Image gallery (main product images) ----------------------------
  // Native horizontal scroll-snap carousel. No dependencies. When there is
  // only one image the slider markup renders without controls, so this is a
  // no-op for single-image kits (i.e. how every existing kit behaves today).
  function initGallery(root) {
    const gallery = root.querySelector('[data-kit-gallery]');
    if (!gallery) return;
    const track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('[data-gallery-slide]'));
    if (slides.length <= 1) return;

    const prev   = gallery.querySelector('[data-gallery-prev]');
    const next   = gallery.querySelector('[data-gallery-next]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));

    function activeIndex() {
      return Math.round(track.scrollLeft / track.clientWidth);
    }
    function goTo(i) {
      const idx = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
    }
    function syncControls() {
      const idx = activeIndex();
      thumbs.forEach((t, i) => {
        const on = i === idx;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
      if (prev) prev.disabled = idx <= 0;
      if (next) next.disabled = idx >= slides.length - 1;
    }

    if (prev) prev.addEventListener('click', () => goTo(activeIndex() - 1));
    if (next) next.addEventListener('click', () => goTo(activeIndex() + 1));
    thumbs.forEach(t => {
      t.addEventListener('click', () => goTo(parseInt(t.dataset.index, 10) || 0));
    });

    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; syncControls(); });
    });
    // Stale-safe: after a theme-editor section reload the old node is detached.
    window.addEventListener('resize', () => {
      if (gallery.isConnected) syncControls();
    });
    syncControls();
  }

  // ---- Sticky offset: auto-measure the theme's header --------------------
  // The site header is a different height at different widths (and may shrink
  // on scroll), so instead of guessing breakpoints we measure whatever is
  // actually pinned at the top and expose it as --kb-header-measured on <html>.
  // CSS then places the sticky heading flush under it (and the preview image a
  // little below). Runs once per page — one header, one set of listeners — and
  // walks down through stacked pinned bars (e.g. announcement + header).
  let stickyOffsetReady = false;
  function setupStickyOffset() {
    if (stickyOffsetReady) return;
    stickyOffsetReady = true;

    function measure() {
      const x = Math.max(1, Math.round(window.innerWidth / 2));
      let y = 1, bottom = 0, guard = 0;
      while (guard++ < 12) {
        let stack = [];
        try { stack = document.elementsFromPoint(x, y) || []; } catch (e) { stack = []; }
        let deepest = 0;
        for (const el of stack) {
          if (!el || !el.closest || el.closest('[data-kit-builder]')) continue;
          const cs = window.getComputedStyle(el);
          if (cs.position === 'fixed' || cs.position === 'sticky') {
            const b = el.getBoundingClientRect().bottom;
            if (b > deepest) deepest = b;
          }
        }
        if (deepest > bottom + 0.5) { bottom = deepest; y = bottom + 1; }
        else break;
      }
      document.documentElement.style.setProperty('--kb-header-measured', Math.round(bottom) + 'px');
    }

    let raf = null;
    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; measure(); });
    }

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', measure);
    // Catch late layout shifts (web fonts, lazy header images).
    setTimeout(measure, 300);
    setTimeout(measure, 1200);
  }

  // ---- Image lightbox -------------------------------------------------
  // Bead photos are small on a 120px tile. Every option gets a magnifier
  // button rendered as a SIBLING of the option (never a child, which would
  // be a button inside a button, or a button inside a label that toggles the
  // radio). Because the trigger sits outside [data-option], the selection
  // handlers simply don't see the click.
  function initZoom(root) {
    let overlay = null;
    let lastFocus = null;

    function build() {
      if (overlay) return overlay;
      overlay = document.createElement('div');
      overlay.className = 'kit-builder__zoom';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.hidden = true;
      overlay.innerHTML =
        '<figure class="kit-builder__zoom-figure">' +
          '<img class="kit-builder__zoom-image" alt="">' +
          '<figcaption class="kit-builder__zoom-caption"></figcaption>' +
        '</figure>' +
        '<button type="button" class="kit-builder__zoom-close" aria-label="Luk">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
          'stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false">' +
          '<path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>';
      overlay.addEventListener('click', function (e) {
        // Backdrop or the close button; clicking the image itself keeps it open.
        if (e.target === overlay || e.target.closest('.kit-builder__zoom-close')) close();
      });
      root.appendChild(overlay);
      return overlay;
    }

    function open(src, title, trigger) {
      const o = build();
      const img = o.querySelector('.kit-builder__zoom-image');
      img.src = src;
      img.alt = title || '';
      o.querySelector('.kit-builder__zoom-caption').textContent = title || '';
      o.hidden = false;
      lastFocus = trigger || null;
      document.documentElement.style.overflow = 'hidden';
      const close_btn = o.querySelector('.kit-builder__zoom-close');
      if (close_btn) close_btn.focus();
    }

    function close() {
      if (!overlay || overlay.hidden) return;
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.isConnected) lastFocus.focus();
      lastFocus = null;
    }

    root.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-zoom-src]');
      if (!trigger || !root.contains(trigger)) return;
      e.preventDefault();
      open(trigger.dataset.zoomSrc, trigger.dataset.zoomTitle || '', trigger);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay && !overlay.hidden) close();
    });
  }

  function initKitBuilder(root) {
    const form        = root.querySelector('[data-kit-form]');
    if (!form) return;
    const slots       = Array.from(root.querySelectorAll('[data-slot]'));
    const qtyInput    = root.querySelector('[data-qty-input]');
    const qtyHelp     = root.querySelector('[data-qty-help]');
    const qtyInc      = root.querySelector('[data-qty-increase]');
    const qtyDec      = root.querySelector('[data-qty-decrease]');
    const totalEl     = root.querySelector('[data-kit-total]');
    const addBtn      = root.querySelector('[data-kit-add]');
    const addLabel    = root.querySelector('[data-kit-add-label]');
    const feedback    = root.querySelector('[data-kit-feedback]');
    const moneyFormat = root.dataset.moneyFormat || '';
    const minKits     = Math.max(1, parseInt(root.dataset.minKits || '1', 10));
    const kitName     = root.dataset.kitName || 'Kit';
    const kitHandle   = root.dataset.kitHandle || '';
    const consumableDays = Math.max(0, parseInt(root.dataset.consumableDays || '0', 10) || 0);

    // Gallery and lightbox are per-builder; the header measurement is
    // page-global (runs once).
    initGallery(root);
    initZoom(root);
    setupStickyOffset();

    // If the section has no blocks configured, the summary won't be rendered.
    if (!addBtn) return;

    // ---- one selection object, whatever kind of step produced it ---------
    // Pack size: prefer per-product (metafield), fall back to step default.
    // Units per kit: always from the step — the same product is used
    // differently in different kits (e.g. 2 beads in earrings, 20 in a
    // necklace), so it's a property of the recipe, not the product. May be
    // fractional (e.g. 0.1 of a 10-metre roll).
    function toSelection(slot, option, units) {
      const optionPackSize = parseInt(option.dataset.packSize, 10);
      const slotPackSize   = parseInt(slot.dataset.packSize || '1', 10);
      return {
        slot: slot,
        none: false,
        variantId: option.dataset.variantId,
        price: parseInt(option.dataset.price, 10),
        inventory: parseInt(option.dataset.inventory, 10),
        inventoryTracked: option.dataset.inventoryTracked === 'true',
        inventoryPolicy: option.dataset.inventoryPolicy,
        title: option.dataset.title,
        unit: option.dataset.unit || 'stk.',
        unitsPerKit: (isFinite(units) && units > 0) ? units : 1,
        packSize: Math.max(1, optionPackSize > 0 ? optionPackSize : slotPackSize)
      };
    }

    // ---- classic step: exactly one radio ---------------------------------
    function readRadioSlot(slot) {
      const required = slot.dataset.required === 'true';
      const checked  = slot.querySelector('input[type="radio"]:checked');
      if (!checked) return { slot: slot, required: required, complete: false, selections: [] };

      const option = checked.closest('[data-option]');

      // "Nej tak" — the customer actively chose to leave this step out. It
      // satisfies a required step (a decision was made), but adds nothing to
      // the price, the cart, or the inventory ceiling.
      if (option.dataset.none === 'true') {
        return { slot: slot, required: required, complete: true, selections: [] };
      }

      const units = parseFloat(slot.dataset.unitsPerKit);
      return {
        slot: slot,
        required: required,
        complete: true,
        selections: [toSelection(slot, option, units)]
      };
    }

    // ---- pool step: N units spread over several products -----------------
    const pools = new Map();

    function poolState(slot) {
      return pools.get(slot);
    }
    // Pack size for one option, resolved the same way toSelection() does it.
    function optionPackSize(slot, option) {
      const own  = parseInt(option.dataset.packSize, 10);
      const step = parseInt(slot.dataset.packSize || '1', 10);
      return Math.max(1, own > 0 ? own : step);
    }

    // Most a single product may contribute: enough to fill the step on its own,
    // and no more, so a stray extra tap can't quietly buy a second bag.
    function maxUnitsFor(slot, option) {
      return Math.max(1, ceilq(poolState(slot).target / optionPackSize(slot, option)));
    }

    // Pieces the customer will actually end up holding, including any they
    // said they already own. This — not the number of taps — is what decides
    // whether the step is satisfied. One bag of 15 covers a 10-piece kit.
    function poolCovered(slot) {
      const state = poolState(slot);
      let pieces = state.own;
      state.picks.forEach(function (count, id) {
        const option = poolOption(slot, id);
        if (option) pieces += count * optionPackSize(slot, option);
      });
      return roundq(pieces);
    }
    // "Resten er mine egne" is measured against what was picked at the time.
    // Remove a pick afterwards and the figure would be stale, so re-cap it.
    function clampOwn(slot) {
      const state = poolState(slot);
      if (!state) return;
      let pieces = 0;
      state.picks.forEach(function (count, id) {
        const option = poolOption(slot, id);
        if (option) pieces += count * optionPackSize(slot, option);
      });
      state.own = Math.max(0, Math.min(state.own, roundq(state.target - pieces)));
    }

    function poolClear(state) {
      state.picks.clear();
      state.order.length = 0;
      state.own = 0;
    }
    function poolOption(slot, id) {
      return slot.querySelector('[data-option][data-variant-id="' + id + '"]');
    }

    function poolAdd(slot, option) {
      const state = poolState(slot);
      if (!state || !option || option.disabled) return;

      // Tapping a product is an implicit "actually, yes please" after Nej tak.
      if (state.mode === 'none') state.mode = 'mix';

      const id     = option.dataset.variantId;
      const perMax = maxUnitsFor(slot, option);

      // "Alle ens": one tap covers the whole step with a single product.
      if (state.mode === 'same') {
        poolClear(state);
        state.picks.set(id, perMax);
        state.order.push(id);
        render();
        return;
      }

      const current = state.picks.get(id) || 0;
      // Already contributing all it can, so a further tap means "undo".
      if (current >= perMax) { poolRemove(slot, id); return; }
      if (!state.picks.has(id)) {
        if (state.order.length >= state.maxDistinct) return;
        state.order.push(id);
      }
      state.picks.set(id, current + 1);
      clampOwn(slot);
      render();
    }

    function poolSub(slot, id) {
      const state = poolState(slot);
      if (!state || !state.picks.has(id)) return;
      const next = state.picks.get(id) - 1;
      if (next <= 0) { poolRemove(slot, id); return; }
      state.picks.set(id, next);
      clampOwn(slot);
      render();
    }

    function poolRemove(slot, id) {
      const state = poolState(slot);
      if (!state) return;
      state.picks.delete(id);
      const at = state.order.indexOf(id);
      if (at > -1) state.order.splice(at, 1);
      clampOwn(slot);
      render();
    }

    function initPool(slot) {
      const choose  = Math.max(1, parseInt(slot.dataset.unitsToChoose || '1', 10));
      const mirror  = Math.max(1, parseInt(slot.dataset.mirror || '1', 10));
      const target  = choose * mirror;
      const maxRaw  = parseInt(slot.dataset.maxDistinct || '0', 10);
      const state = {
        // How many pieces the finished kit needs, in total.
        target: target,
        // Blank or 0 means "as many different products as there are pieces to
        // fill" — no reason to stop someone buying ten different beads.
        maxDistinct: (isFinite(maxRaw) && maxRaw > 0) ? Math.min(maxRaw, target) : target,
        allowOwn: slot.dataset.allowOwn === 'true',
        allowNone: slot.dataset.allowNone === 'true',
        piecesLabel: slot.dataset.piecesLabel || 'stk.',
        ownLabel: 'Egne perler',
        noneLabel: slot.dataset.noneLabel || 'Nej tak',
        // A step that only allows one distinct product is an "all the same"
        // step by definition, so skip straight to that mode.
        mode: 'mix',
        picks: new Map(),
        order: [],
        own: 0
      };
      if (state.maxDistinct <= 1) state.mode = 'same';
      pools.set(slot, state);

      const grid = slot.querySelector('[data-pool-grid]');
      if (grid) {
        grid.addEventListener('click', function (e) {
          const option = e.target.closest('[data-option]');
          if (option && grid.contains(option)) poolAdd(slot, option);
        });
      }

      slot.querySelectorAll('[data-pool-mode]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const next = btn.dataset.poolMode;
          state.mode = (next === 'same' || next === 'none') ? next : 'mix';
          poolClear(state);
          render();
        });
      });

      const reset = slot.querySelector('[data-pool-reset]');
      if (reset) {
        reset.addEventListener('click', function () { poolClear(state); render(); });
      }

      const ownBtn = slot.querySelector('[data-pool-own]');
      if (ownBtn) {
        ownBtn.addEventListener('click', function () {
          state.own = state.own > 0 ? 0 : Math.max(0, roundq(state.target - poolCovered(slot)));
          render();
        });
      }

      // The chosen list is re-rendered on every change, so use delegation
      // rather than re-binding buttons that no longer exist.
      const lines = slot.querySelector('[data-pool-lines]');
      if (lines) {
        lines.addEventListener('click', function (e) {
          const inc = e.target.closest('[data-pool-inc]');
          if (inc) { poolAdd(slot, poolOption(slot, inc.dataset.poolInc)); return; }
          const dec = e.target.closest('[data-pool-dec]');
          if (dec) { poolSub(slot, dec.dataset.poolDec); return; }
          const del = e.target.closest('[data-pool-del]');
          if (del) { poolRemove(slot, del.dataset.poolDel); return; }
          const ownDel = e.target.closest('[data-pool-own-del]');
          if (ownDel) { state.own = 0; render(); }
        });
      }
    }

    function readPoolSlot(slot) {
      const state    = poolState(slot);
      const required = slot.dataset.required === 'true';
      if (!state) return { slot: slot, required: required, complete: false, selections: [] };

      // "Nej tak" to the whole step: a decision was made, nothing is bought.
      if (state.mode === 'none') {
        return { slot: slot, required: required, complete: true, selections: [] };
      }

      const picks = [];
      state.order.forEach(function (id) {
        const count = state.picks.get(id);
        if (!count) return;
        const option = poolOption(slot, id);
        if (!option) return;
        const sel = toSelection(slot, option, 1);
        sel.units = count;
        // Pieces the customer's taps put on the table for this product.
        sel.chosenPieces = roundq(count * sel.packSize);
        picks.push(sel);
      });

      // A pick is "I want this product in the mix", not "buy me one bag per
      // kit". So the taps set the RATIO and the step's requirement sets the
      // amount: each product supplies its share of the pieces one kit needs.
      // Packs are then worked out from that share at whatever quantity is
      // ordered, which is why 3 kits needing 30 beads buy 2 bags of 15 rather
      // than 3. Storing bags-per-kit instead made the price scale with the
      // number of kits while ignoring how many beads they actually contain.
      const chosenPieces = picks.reduce(function (sum, sel) {
        return roundq(sum + sel.chosenPieces);
      }, 0);

      // Capped at what was actually chosen, so a half-finished step shows the
      // one bag the customer picked rather than inflating to the full target.
      const fromProducts = Math.max(0, Math.min(roundq(state.target - state.own), chosenPieces));

      const selections = [];
      picks.forEach(function (sel) {
        sel.unitsPerKit = chosenPieces > 0
          ? roundq(fromProducts * sel.chosenPieces / chosenPieces)
          : 0;
        if (sel.unitsPerKit > 0) selections.push(sel);
      });

      return {
        slot: slot,
        required: required,
        complete: poolCovered(slot) >= state.target,
        selections: selections
      };
    }

    function readSlot(slot) {
      return slot.dataset.slotMode === 'pool' ? readPoolSlot(slot) : readRadioSlot(slot);
    }

    // ---- shared maths (unchanged shape, now fed by both step types) ------
    function maxKitsAllowed(selections) {
      if (!selections.length) return Infinity;
      return selections.reduce((min, sel) => {
        if (sel.none) return min;
        const unbounded = !sel.inventoryTracked || sel.inventoryPolicy === 'continue';
        if (unbounded) return min;
        const piecesOnHand = Math.max(0, sel.inventory) * sel.packSize;
        const possible = floorq(piecesOnHand / sel.unitsPerKit);
        return Math.min(min, possible);
      }, Infinity);
    }

    function computeTotal(selections, kitQty) {
      return selections.reduce((sum, sel) => {
        if (sel.none) return sum;
        const piecesNeeded = roundq(sel.unitsPerKit * kitQty);
        const packsNeeded  = ceilq(piecesNeeded / sel.packSize);
        return sum + (packsNeeded * sel.price);
      }, 0);
    }

    // ---- pool rendering --------------------------------------------------
    function renderPool(slot, read, kitQty) {
      const state = poolState(slot);
      if (!state) return;

      const skipped = state.mode === 'none';
      const covered = skipped ? 0 : poolCovered(slot);
      const missing = Math.max(0, roundq(state.target - covered));
      const enough  = covered >= state.target;

      slot.classList.toggle('is-skipped', skipped);

      const bar = slot.querySelector('[data-pool-bar]');
      if (bar) bar.style.width = Math.min(100, Math.round(covered / state.target * 100)) + '%';

      const tally = slot.querySelector('[data-pool-tally]');
      if (tally) {
        tally.classList.toggle('is-enough', enough && !skipped);
        if (skipped) {
          tally.textContent = state.noneLabel;
        } else if (enough) {
          // Deliberately not "10 af 10": one bag of 15 covers a 10-piece kit,
          // and telling someone they're short when they're holding 75 beads
          // is just wrong.
          tally.textContent = 'Nok ' + state.piecesLabel + ' til dit sæt';
        } else {
          tally.textContent = covered + ' af ' + state.target + ' ' + state.piecesLabel;
        }
      }

      // Tile state: how many selling units of it, and a lock once the step has
      // as many different products as it allows.
      const full = state.order.length >= state.maxDistinct;
      slot.querySelectorAll('[data-pool-grid] [data-option]').forEach(function (option) {
        const id    = option.dataset.variantId;
        const count = state.picks.get(id) || 0;
        const badge = option.querySelector('[data-option-count]');
        if (badge) badge.textContent = count ? ('\u00d7' + count) : '';
        option.classList.toggle('is-chosen', count > 0);
        option.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');
        option.classList.toggle('is-locked', !skipped && !option.disabled && count === 0 && full);
      });

      slot.querySelectorAll('[data-pool-mode]').forEach(function (btn) {
        const on = btn.dataset.poolMode === state.mode;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      const touched = state.order.length > 0 || state.own > 0;

      const reset = slot.querySelector('[data-pool-reset]');
      if (reset) reset.hidden = !touched && !skipped;

      const chosen = slot.querySelector('[data-pool-chosen]');
      if (chosen) chosen.hidden = skipped || !touched;

      // Line items, running totals and the "what you actually buy" note.
      let sum = 0, packs = 0, pieces = 0;
      const units = new Set();
      const rows = [];

      read.selections.forEach(function (sel) {
        const piecesNeeded = roundq(sel.unitsPerKit * kitQty);
        const packsNeeded  = ceilq(piecesNeeded / sel.packSize);
        const linePrice    = packsNeeded * sel.price;

        sum    += linePrice;
        packs  += packsNeeded;
        pieces += packsNeeded * sel.packSize;
        units.add(sel.unit);

        const option = poolOption(slot, sel.variantId);
        const img    = option ? option.querySelector('img') : null;
        const count  = state.picks.get(sel.variantId) || 0;

        // The stepper sets this product's share of the mix. When one selling
        // unit already covers the whole step there is no share to set, so it
        // would be a control that can't do anything sitting next to the number
        // that actually matters. Hide it and let the pack count speak.
        const perMax = option ? maxUnitsFor(slot, option) : 1;
        const showStepper = state.mode === 'mix' && perMax > 1;

        rows.push(
          '<div class="kit-builder__pool-line">' +
            '<span class="kit-builder__pool-line-media">' +
              (img ? '<img src="' + esc(img.getAttribute('src')) + '" alt="" width="48" height="48" loading="lazy">' : '') +
            '</span>' +
            '<span class="kit-builder__pool-line-title">' + esc(sel.title) + '</span>' +
            (showStepper
              ? '<span class="kit-builder__pool-line-qty">' +
                  '<button type="button" data-pool-dec="' + esc(sel.variantId) + '" aria-label="Mindre ' + esc(sel.title) + ' i blandingen">\u2212</button>' +
                  '<span>' + count + '</span>' +
                  '<button type="button" data-pool-inc="' + esc(sel.variantId) + '" aria-label="Mere ' + esc(sel.title) + ' i blandingen"' +
                    (count >= perMax ? ' disabled' : '') + '>+</button>' +
                '</span>'
              : '') +
            // What the customer is actually buying, at the current set count.
            '<span class="kit-builder__pool-line-count">' + packsNeeded + ' ' +
              esc(pluralUnit(sel.unit, packsNeeded)) + '</span>' +
            '<span class="kit-builder__pool-line-price">' + formatMoney(linePrice, moneyFormat) + '</span>' +
            '<button type="button" class="kit-builder__pool-line-del" data-pool-del="' + esc(sel.variantId) + '" ' +
              'aria-label="Fjern ' + esc(sel.title) + '">' + TRASH_SVG + '</button>' +
          '</div>'
        );
      });

      if (state.own > 0) {
        rows.push(
          '<div class="kit-builder__pool-line kit-builder__pool-line--own">' +
            '<span class="kit-builder__pool-line-media kit-builder__pool-line-media--own"></span>' +
            '<span class="kit-builder__pool-line-title">' + esc(state.ownLabel) + '</span>' +
            '<span class="kit-builder__pool-line-count">' + roundq(state.own * kitQty) + ' ' +
              esc(state.piecesLabel) + '</span>' +
            '<span class="kit-builder__pool-line-price">' + formatMoney(0, moneyFormat) + '</span>' +
            '<button type="button" class="kit-builder__pool-line-del" data-pool-own-del ' +
              'aria-label="Fjern ' + esc(state.ownLabel) + '">' + TRASH_SVG + '</button>' +
          '</div>'
        );
      }

      const lines = slot.querySelector('[data-pool-lines]');
      if (lines) lines.innerHTML = rows.join('');

      const subtotal = slot.querySelector('[data-pool-subtotal]');
      if (subtotal) subtotal.textContent = read.selections.length ? formatMoney(sum, moneyFormat) : '\u2014';

      const note = slot.querySelector('[data-pool-note]');
      if (note) {
        const parts = [];
        if (packs > 0) {
          const unitWord = units.size === 1 ? pluralUnit(Array.from(units)[0], packs) : pluralUnit('pakke', packs);
          parts.push('Du køber ' + packs + ' ' + unitWord + ' = ' + Math.round(pieces) + ' ' + state.piecesLabel + '.');
          parts.push((kitQty === 1 ? 'Sættet skal bruge ' : kitQty + ' sæt skal bruge ') +
            roundq(state.target * kitQty) + '.');
        }
        // Everything in this sentence is for the whole order, so the customer's
        // own pieces and any shortfall scale with the number of sets too.
        // Mixing per-kit and per-order figures made the numbers not add up.
        if (state.own > 0) {
          parts.push('Du lægger selv ' + roundq(state.own * kitQty) + ' ' + state.piecesLabel + ' til.');
        }
        if (missing > 0) {
          parts.push('Mangler ' + roundq(missing * kitQty) + ' ' + state.piecesLabel + '.');
        }
        note.textContent = skipped ? '' : parts.join(' ');
      }

      const ownBtn = slot.querySelector('[data-pool-own]');
      if (ownBtn) {
        ownBtn.hidden = !(state.allowOwn && state.mode === 'mix' && missing > 0);
      }

      const noneBtn = slot.querySelector('[data-pool-mode="none"]');
      if (noneBtn) {
        noneBtn.classList.toggle('is-active', skipped);
        noneBtn.setAttribute('aria-pressed', skipped ? 'true' : 'false');
      }
    }


    // ---- Tools shelf -----------------------------------------------------
    // Not steps: never required, never blocking, quantity always 1. Ownership
    // is remembered per tool TYPE rather than per product, so owning a
    // fladtang counts as owning a fladtang even if a later kit recommends a
    // different one.
    const toolEls = Array.from(root.querySelectorAll('[data-tool]'));

    function ownKey(type) {
      return 'kb:tool:' + type;
    }

    function readOwned(el) {
      const type = el.dataset.toolType;
      if (!type) return false;
      let raw = null;
      try { raw = window.localStorage.getItem(ownKey(type)); } catch (e) { raw = null; }
      if (!raw) return false;
      // Consumables expire so we ask again once the glue is likely used up.
      if (el.dataset.consumable === 'true' && consumableDays > 0) {
        const when = parseInt(raw, 10);
        if (!isFinite(when)) return false;
        const age = (Date.now() - when) / 86400000;
        if (age > consumableDays) {
          try { window.localStorage.removeItem(ownKey(type)); } catch (e) {}
          return false;
        }
      }
      return true;
    }

    function writeOwned(el, owned) {
      const type = el.dataset.toolType;
      if (!type) return;
      try {
        if (owned) window.localStorage.setItem(ownKey(type), String(Date.now()));
        else window.localStorage.removeItem(ownKey(type));
      } catch (e) { /* private mode: the session still works, just isn't remembered */ }
    }

    function initTools() {
      const toggle = root.querySelector('[data-tools-toggle]');
      if (toggle && toolsBox) {
        toggle.addEventListener('click', function () {
          toolsBox.dataset.expanded = toolsBox.dataset.expanded === 'true' ? 'false' : 'true';
          render();
        });
      }

      toolEls.forEach(function (el) {
        // Order history is the durable layer; localStorage is the convenience
        // one. Either is enough to consider the tool owned on first paint.
        const remembered = readOwned(el);
        const fromHistory = el.dataset.ownedBefore === 'true';
        el.dataset.owned = (remembered || fromHistory) ? 'true' : 'false';
        if (fromHistory && !remembered) writeOwned(el, true);

        const check = el.querySelector('[data-tool-check]');
        if (check) check.addEventListener('change', render);

        const ownBtn = el.querySelector('[data-tool-own]');
        if (ownBtn) {
          ownBtn.addEventListener('click', function () {
            if (ownBtn.dataset.action === 'reveal') {
              el.dataset.revealed = 'true';
              render();
              return;
            }
            const nowOwned = el.dataset.owned !== 'true';
            el.dataset.owned = nowOwned ? 'true' : 'false';
            // Marking it owned folds the row away again; saying you don't have
            // it keeps it open.
            el.dataset.revealed = nowOwned ? 'false' : 'true';
            writeOwned(el, nowOwned);
            // Owning it means not buying it again.
            if (nowOwned && check) check.checked = false;
            render();
          });
        }
      });
    }

    // ---- what the customer already has ----------------------------------
    // Progress is counted in TYPES, not products, because one multi-tool can
    // do four jobs. That also means a customer holding a multi-tang is never
    // asked to buy a separate fladtang.
    const toolsBox = root.querySelector('[data-kit-tools]');

    let cartTypes = new Set();
    let cartProductIds = new Set();

    // One tool, one job. Multi-tools used to declare several and it cost more
    // in confusion than it saved in taps: a customer who owns an all-in-one
    // just presses "Jeg har den allerede" once and, since that's remembered by
    // type, is never asked again.
    function typeOf(el) {
      return (el.dataset.toolType || '').toLowerCase();
    }

    // Deliberately narrow: only whether THIS product is in the basket. A
    // different product covering the same job is handled through the covered
    // set instead, so we never tell someone a fladtang is "i kurven" when what
    // they actually have in there is the multi-tang.
    function inCart(el) {
      return cartProductIds.has(el.dataset.productId);
    }

    async function refreshCartTypes() {
      try {
        const resp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!resp.ok) return;
        const cart = await resp.json();
        const types = new Set();
        const ids = new Set();
        (cart.items || []).forEach(function (item) {
          const pid = String(item.product_id);
          ids.add(pid);
          // Tools added through the builder carry their type, which is the
          // only way to recognise a tool the shelf doesn't happen to list.
          const stamped = item.properties && item.properties._tool_type;
          if (stamped) {
            String(stamped).split('|').forEach(function (t) {
              const v = t.trim().toLowerCase();
              if (v) types.add(v);
            });
          }
        });
        // A listed tool sitting in the cart covers its job.
        toolEls.forEach(function (el) {
          if (!ids.has(el.dataset.productId)) return;
          const t = typeOf(el);
          if (t) types.add(t);
        });
        cartTypes = types;
        cartProductIds = ids;
        render();
      } catch (e) { /* no cart info just means we ask as usual */ }
    }

    function readTools() {
      const chosen = [];
      toolEls.forEach(function (el) {
        const check = el.querySelector('[data-tool-check]');
        if (!check || !check.checked) return;
        chosen.push({
          variantId: el.dataset.variantId,
          price: parseInt(el.dataset.price, 10) || 0,
          title: el.dataset.title || '',
          types: typeOf(el)
        });
      });
      return chosen;
    }

    function renderTools() {
      if (!toolEls.length) return;

      // Pass 1: what does the customer already have covered, and what jobs
      // does this shelf cover in total.
      // Jobs already handled by whatever is in the basket, whichever product
      // is doing the job.
      const covered = new Set(cartTypes);
      const needed = new Set(); // filled after the first pass, see below
      let anyPicked = false;
      let anyOwned = false;
      let anyInCart = false;
      let overridden = false;

      const state = toolEls.map(function (el) {
        const check = el.querySelector('[data-tool-check]');
        const owned = el.dataset.owned === 'true';
        const carted = inCart(el);
        const picked = !!(check && check.checked);
        const type = typeOf(el);

        if (picked) anyPicked = true;
        if (owned) anyOwned = true;
        if (carted) anyInCart = true;
        if (cartTypes.size > 0) anyInCart = true;
        // A row the customer explicitly opened keeps the shelf open.
        if (el.dataset.revealed === 'true') overridden = true;

        // Picked counts as covered too: it's on its way into the basket.
        if (type && (owned || carted || picked)) covered.add(type);
        if (type) needed.add(type);

        return { el: el, check: check, owned: owned, carted: carted, picked: picked, type: type };
      });

      // Pass 2: a row is settled when every job it does is already handled,
      // whether by this product or by another one. That's what stops us
      // offering a fladtang to someone who just picked the multi-tang.
      state.forEach(function (row) {
        // Two products of the same type (say two fladtange in different
        // colours) settle each other, which is the whole point of typing them.
        const satisfied = !!row.type && covered.has(row.type);
        // "Vis" opens a settled row back up without claiming anything about
        // ownership, so someone can still buy a spare on purpose.
        const revealed = row.el.dataset.revealed === 'true';
        const settled = satisfied && !row.picked && !revealed;

        row.el.classList.toggle('is-owned', row.owned);
        row.el.classList.toggle('is-in-cart', row.carted && !row.owned);
        row.el.classList.toggle('is-covered', settled);
        row.el.classList.toggle('is-picked', row.picked);

        if (row.check) row.check.disabled = settled || row.carted;

        const status = row.el.querySelector('[data-tool-status]');
        if (status) {
          if (row.carted) status.textContent = 'I kurven';
          else if (row.owned) status.textContent = 'Du har den';
          else if (satisfied && !row.picked) status.textContent = 'Dækket';
          else status.textContent = '';
        }

        // One button, three jobs, depending on what the row currently is.
        const ownBtn = row.el.querySelector('[data-tool-own]');
        if (ownBtn) {
          if (settled) {
            ownBtn.hidden = false;
            ownBtn.dataset.action = 'reveal';
            ownBtn.textContent = 'Vis';
            ownBtn.removeAttribute('aria-pressed');
          } else if (row.carted) {
            // Nothing sensible to offer: it's demonstrably in the basket.
            ownBtn.hidden = true;
          } else {
            ownBtn.hidden = false;
            ownBtn.dataset.action = 'own';
            ownBtn.textContent = row.owned ? 'Jeg har den ikke' : 'Jeg har den allerede';
            ownBtn.setAttribute('aria-pressed', row.owned ? 'true' : 'false');
          }
        }

        // Editor-only: the resolved job list. Getting the multi-tool map or the
        // metafield wrong is otherwise invisible until the maths looks wrong.
        const debug = row.el.querySelector('[data-tool-debug]');
        if (debug) {
          debug.textContent = 'Type: ' + (row.type || 'INGEN — sæt custom.vaerktoj_type');
        }
      });

      let done = 0;
      needed.forEach(function (t) { if (covered.has(t)) done++; });
      const total = needed.size;

      // Editor-only: what the shelf decided this kit requires, and why.
      const boxDebug = root.querySelector('[data-tools-debug]');
      if (boxDebug) {
        boxDebug.textContent = 'Kittet kræver: ' +
          (total ? Array.from(needed).join(', ') : 'intet') +
          '. Værktøj med samme type dækker hinanden.';
      }
      const complete = total > 0 && done === total;

      const bar = root.querySelector('[data-tools-bar]');
      if (bar) bar.style.width = (total ? Math.round(done / total * 100) : 0) + '%';

      const tally = root.querySelector('[data-tools-tally]');
      if (tally) {
        tally.classList.toggle('is-enough', complete);
        tally.textContent = done + ' af ' + total + ' klaret';
      }

      // Collapse only when there is nothing left to do AND nothing on its way
      // to the basket — a customer who just added a tool wants to see it.
      const collapsible = complete && !anyPicked && !overridden;
      if (toolsBox) toolsBox.classList.toggle('is-complete', collapsible);

      const list = root.querySelector('[data-tools-list]');
      if (list) list.hidden = collapsible && toolsBox.dataset.expanded !== 'true';

      const intro = root.querySelector('[data-tools-intro]');
      if (intro) intro.hidden = complete;

      const doneMsg = root.querySelector('[data-tools-done]');
      if (doneMsg) {
        doneMsg.hidden = !complete;
        if (anyOwned && anyInCart) {
          doneMsg.textContent = 'Du har alt det værktøj du skal bruge til dette smykke, dels i forvejen og dels i kurven.';
        } else if (anyInCart && !anyOwned) {
          doneMsg.textContent = 'Alt værktøjet til dette smykke ligger allerede i din kurv.';
        } else if (anyPicked && !anyOwned) {
          doneMsg.textContent = 'Nu har du alt det værktøj du skal bruge til dette smykke.';
        } else {
          doneMsg.textContent = 'Du har allerede alt det værktøj du skal bruge til dette smykke.';
        }
      }

      const toggle = root.querySelector('[data-tools-toggle]');
      if (toggle) {
        toggle.hidden = !collapsible;
        const shown = toolsBox && toolsBox.dataset.expanded === 'true';
        toggle.textContent = shown ? 'Skjul værktøj' : 'Vis værktøj';
        toggle.setAttribute('aria-expanded', shown ? 'true' : 'false');
      }
    }



    // Anything the customer has actively decided, across both step types and
    // the tool shelf. Drives the preset box's secondary button.
    function hasAnySelection() {
      if (root.querySelector('[data-slot] input[type="radio"]:checked')) return true;
      let any = false;
      slots.forEach(function (slot) {
        const state = poolState(slot);
        if (!state) return;
        if (state.order.length > 0 || state.own > 0 || state.mode === 'none') any = true;
      });
      if (any) return true;
      return toolEls.some(function (el) {
        const check = el.querySelector('[data-tool-check]');
        return check && check.checked;
      });
    }

    function resetAll() {
      root.querySelectorAll('[data-slot] input[type="radio"]').forEach(function (radio) {
        radio.checked = false;
      });
      slots.forEach(function (slot) {
        const state = poolState(slot);
        if (!state) return;
        poolClear(state);
        state.mode = state.maxDistinct <= 1 ? 'same' : 'mix';
      });
      // Untick the tools, but never forget what the customer says they own.
      toolEls.forEach(function (el) {
        const check = el.querySelector('[data-tool-check]');
        if (check) check.checked = false;
      });
      qtyInput.value = minKits;

      if (presetEl) {
        presetEl.dataset.applied = 'false';
        const err = presetEl.querySelector('[data-preset-error]');
        if (err) { err.textContent = ''; err.removeAttribute('data-state'); }
      }
      if (feedback) { feedback.textContent = ''; feedback.removeAttribute('data-state'); }

      // Drop a shared ?kit= from the address bar, otherwise a reload would
      // silently rebuild what the customer just cleared.
      try {
        if (window.location.search && window.history.replaceState) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch (e) {}

      render();
      scrollToFirstStep();
    }

    function scrollToFirstStep() {
      const target = root.querySelector('.kit-builder__slot:not(.is-complete)') || slots[0];
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // ---- Configuration <-> URL ------------------------------------------
    // Format: ?kit=<step>x<variantId>[*units][,...] . <step>x... & s=<sets>
    //         & v=<toolVariantId>,...
    // "n" means the step was declined, "o<n>" is pieces the customer supplies.
    // Readable enough to eyeball in an ad URL, short enough to paste.
    // Separators are limited to characters URLSearchParams leaves alone
    // (alphanumerics and * - . _), so an advertised link stays readable
    // instead of filling up with %2C.
    function configString() {
      const parts = [];
      slots.forEach(function (slot, i) {
        const step = i + 1;
        if (slot.dataset.slotMode === 'pool') {
          const state = poolState(slot);
          if (!state) return;
          if (state.mode === 'none') { parts.push(step + 'xn'); return; }
          const bits = [];
          state.order.forEach(function (id) {
            const count = state.picks.get(id);
            if (count) bits.push(count > 1 ? (id + '*' + count) : id);
          });
          if (state.own > 0) bits.push('o' + state.own);
          if (bits.length) parts.push(step + 'x' + bits.join('-'));
        } else {
          const checked = slot.querySelector('input[type="radio"]:checked');
          if (!checked) return;
          const option = checked.closest('[data-option]');
          if (option.dataset.none === 'true') parts.push(step + 'xn');
          else parts.push(step + 'x' + option.dataset.variantId);
        }
      });

      return parts.join('.');
    }

    function encodeConfig() {
      const params = new URLSearchParams();
      const config = configString();
      if (config) params.set('kit', config);
      const qty = parseInt(qtyInput.value, 10) || minKits;
      if (qty !== minKits) params.set('s', String(qty));
      const tools = readTools().map(function (t) { return t.variantId; });
      if (tools.length) params.set('v', tools.join(','));

      const base = window.location.origin + window.location.pathname;
      const qs = params.toString();
      return qs ? (base + '?' + qs) : base;
    }

    // Returns the titles of anything in the config we couldn't apply, so the
    // caller can tell the customer instead of silently building something else.
    function applyConfig(kitParam, setsParam, toolsParam) {
      const missing = [];

      (kitParam || '').split('.').forEach(function (segment) {
        if (!segment) return;
        const at = segment.indexOf('x');
        if (at < 1) return;
        const step = parseInt(segment.slice(0, at), 10);
        const body = segment.slice(at + 1);
        const slot = slots[step - 1];
        if (!slot) return;

        if (slot.dataset.slotMode === 'pool') {
          const state = poolState(slot);
          if (!state) return;
          poolClear(state);
          if (body === 'n') {
            state.mode = state.allowNone ? 'none' : 'mix';
            return;
          }
          state.mode = 'mix';
          body.split('-').forEach(function (bit) {
            if (!bit) return;
            if (bit.charAt(0) === 'o') {
              state.own = Math.max(0, parseInt(bit.slice(1), 10) || 0);
              return;
            }
            const star = bit.indexOf('*');
            const id = star > -1 ? bit.slice(0, star) : bit;
            const count = star > -1 ? (parseInt(bit.slice(star + 1), 10) || 1) : 1;
            const option = poolOption(slot, id);
            // Unavailable options aren't rendered at all, so a missing node
            // means the part is sold out or was removed from the pool.
            if (!option || option.disabled) { missing.push(slot.dataset.slotLabel || 'Et trin'); return; }
            state.order.push(id);
            state.picks.set(id, Math.min(count, maxUnitsFor(slot, option)));
          });
        } else {
          if (body === 'n') {
            const noneInput = slot.querySelector('[data-option][data-none="true"] input[type="radio"]');
            if (noneInput) noneInput.checked = true;
            else missing.push(slot.dataset.slotLabel || 'Et trin');
            return;
          }
          const input = slot.querySelector('[data-option][data-variant-id="' + body + '"] input[type="radio"]');
          if (input && !input.disabled) input.checked = true;
          else missing.push(slot.dataset.slotLabel || 'Et trin');
        }
      });

      if (setsParam) {
        const n = parseInt(setsParam, 10);
        if (isFinite(n) && n >= minKits) qtyInput.value = n;
      }

      if (toolsParam) {
        const wanted = toolsParam.split(',');
        toolEls.forEach(function (el) {
          if (wanted.indexOf(el.dataset.variantId) === -1) return;
          const check = el.querySelector('[data-tool-check]');
          // A remembered tool stays un-ticked: they already own it.
          if (check && !check.disabled) check.checked = true;
        });
      }

      render();
      return missing;
    }

    // ---- Preset ----------------------------------------------------------
    const presetEl = root.querySelector('[data-kit-preset]');

    // Two builds are the same build regardless of the order the customer
    // tapped things in, so sort before comparing.
    function normaliseConfig(str) {
      return (str || '').split('.').filter(Boolean).map(function (segment) {
        const at = segment.indexOf('x');
        if (at < 1) return segment;
        const bits = segment.slice(at + 1).split('-').filter(Boolean).sort();
        return segment.slice(0, at) + 'x' + bits.join('-');
      }).sort().join('.');
    }

    function presetConfig() {
      const parts = [];
      slots.forEach(function (slot, i) {
        const step = i + 1;
        if (slot.dataset.slotMode === 'pool') {
          const raw = slot.dataset.presetVariants;
          if (!raw) return;
          parts.push(step + 'x' + (raw === 'none' ? 'n' : raw.split(',').join('-')));
        } else {
          const raw = slot.dataset.presetVariant;
          if (!raw) return;
          parts.push(step + 'x' + (raw === 'none' ? 'n' : raw));
        }
      });
      return parts.join('.');
    }

    function initPreset() {
      if (!presetEl) return;
      const config = presetConfig();
      if (!config) return;
      presetEl.hidden = false;

      const applyBtn = presetEl.querySelector('[data-preset-apply]');
      const secondaryBtn = presetEl.querySelector('[data-preset-secondary]');
      const errorEl = presetEl.querySelector('[data-preset-error]');

      if (applyBtn) {
        applyBtn.addEventListener('click', function () {
          const missing = applyConfig(config, null, null);
          if (missing.length) {
            // Explicit rather than silent: a half-applied preset that looks
            // finished is worse than no preset.
            const unique = missing.filter(function (v, i, a) { return a.indexOf(v) === i; });
            errorEl.textContent = 'Nogle af delene på billedet er udsolgt: ' + unique.join(', ') +
              '. Resten er udfyldt, vælg selv de sidste.';
            errorEl.setAttribute('data-state', 'error');
          } else {
            errorEl.textContent = '';
            errorEl.removeAttribute('data-state');
          }
          presetEl.dataset.applied = 'true';
          markPresetState();
          scrollToFirstStep();
        });
      }

      if (secondaryBtn) {
        secondaryBtn.addEventListener('click', function () {
          // Nothing chosen yet: just get them started. Something chosen: this
          // is the way back to a clean slate.
          if (hasAnySelection()) resetAll();
          else scrollToFirstStep();
        });
      }
    }

    function markPresetState() {
      if (!presetEl) return;
      const any = hasAnySelection();

      const secondary = presetEl.querySelector('[data-preset-secondary]');
      if (secondary) {
        secondary.textContent = any ? 'Nulstil alle valg' : 'Vælg selv';
      }

      const status = presetEl.querySelector('[data-preset-status]');
      if (!status) return;
      if (!any) {
        status.textContent = status.dataset.helpDefault || '';
        return;
      }
      const same = normaliseConfig(configString()) === normaliseConfig(presetConfig());
      status.textContent = same
        ? 'Dit sæt er som på billedet.'
        : 'Du har tilpasset sættet. Tryk på "' +
          (presetEl.querySelector('[data-preset-apply]') || {}).textContent.trim() +
          '" for at gå tilbage til billedet.';
    }

    // ---- Share -----------------------------------------------------------
    function initShare() {
      const shareBtn = root.querySelector('[data-kit-share]');
      if (!shareBtn) return;
      const label = shareBtn.querySelector('[data-share-label]');
      const original = label ? label.textContent : '';

      shareBtn.addEventListener('click', async function () {
        const url = encodeConfig();
        try {
          if (navigator.share) {
            await navigator.share({ title: kitName, url: url });
            return;
          }
          await navigator.clipboard.writeText(url);
          if (label) {
            label.textContent = 'Link kopieret';
            setTimeout(function () { label.textContent = original; }, 2000);
          }
        } catch (err) {
          // AbortError just means the customer closed the share sheet.
          if (err && err.name === 'AbortError') return;
          if (label) {
            label.textContent = 'Kunne ikke kopiere';
            setTimeout(function () { label.textContent = original; }, 2000);
          }
        }
      });
    }

    // ---- master render ---------------------------------------------------
    function render() {
      const reads = slots.map(readSlot);
      const selections = reads.reduce(function (all, read) {
        return all.concat(read.selections);
      }, []);

      reads.forEach(function (read) {
        read.slot.classList.toggle('is-complete', read.complete);
      });

      const complete = reads.every(function (read) {
        return !read.required || read.complete;
      });
      const maxKits = maxKitsAllowed(selections);

      let qty = parseInt(qtyInput.value, 10);
      if (isNaN(qty) || qty < minKits) qty = minKits;
      if (maxKits !== Infinity && qty > maxKits) qty = Math.max(minKits, maxKits);
      qtyInput.value = qty;
      qtyInput.min = minKits;
      if (maxKits !== Infinity) qtyInput.max = maxKits; else qtyInput.removeAttribute('max');

      reads.forEach(function (read) {
        if (read.slot.dataset.slotMode === 'pool') renderPool(read.slot, read, qty);
      });

      if (!complete) {
        qtyHelp.textContent = 'Vælg alle dele først.';
      } else if (!selections.length) {
        qtyHelp.textContent = 'Vælg mindst én del til dit sæt.';
      } else if (maxKits === Infinity) {
        qtyHelp.textContent = '';
      } else if (maxKits < minKits) {
        qtyHelp.textContent = 'Desværre er en eller flere af de valgte dele udsolgt.';
      } else {
        qtyHelp.textContent = `Op til ${maxKits} sæt på lager med dit nuværende valg.`;
      }

      const tools = readTools();
      renderTools();
      const toolsTotal = tools.reduce(function (sum, t) { return sum + t.price; }, 0);

      if (!complete || !selections.length) {
        totalEl.textContent = '—';
      } else {
        totalEl.textContent = formatMoney(computeTotal(selections, qty) + toolsTotal, moneyFormat);
      }

      // Tools don't scale with the number of sets, so say so rather than
      // letting the total look like it multiplied oddly.
      const toolsSum = root.querySelector('[data-tools-sum]');
      if (toolsSum) {
        toolsSum.hidden = toolsTotal === 0;
        toolsSum.textContent = toolsTotal === 0 ? '' :
          'Heraf værktøj: ' + formatMoney(toolsTotal, moneyFormat) + ' (købes én gang, uanset antal sæt)';
      }

      markPresetState();

      const canAdd = complete
        && selections.length > 0
        && qty >= minKits
        && (maxKits === Infinity || qty <= maxKits)
        && maxKits >= minKits;
      addBtn.disabled = !canAdd;
    }

    function buildCartItems(selections, kitQty, tools) {
      const byVariant = new Map();
      selections.forEach(sel => {
        if (sel.none) return;
        const piecesNeeded = roundq(sel.unitsPerKit * kitQty);
        const packsNeeded  = ceilq(piecesNeeded / sel.packSize);
        if (packsNeeded <= 0) return;
        const prev = byVariant.get(sel.variantId) || 0;
        byVariant.set(sel.variantId, prev + packsNeeded);
      });

      const kitId = '#' + Math.random().toString(36).slice(2, 8).toUpperCase();

      // _kit_id is what the order tagging rule keys on — do not rename it.
      // _kit_handle is hidden too and gives kit-level revenue in Shopify's own
      // reports, which is otherwise impossible because the kit product itself
      // never enters the cart.
      function kitProps() {
        const props = {
          '_kit_id': kitId,
          'Kit': kitName + ' ' + kitId
        };
        if (kitHandle) props['_kit_handle'] = kitHandle;
        return props;
      }

      const items = [];
      byVariant.forEach((quantity, id) => {
        items.push({ id: parseInt(id, 10), quantity: quantity, properties: kitProps() });
      });

      // Tools are per customer, not per kit: three sets still need one pair of
      // pliers, so the quantity stays 1 however many kits are ordered.
      (tools || []).forEach(tool => {
        const props = kitProps();
        // Hidden. Lets a later page load see this tool is already handled,
        // which /cart.js alone can't tell us since it carries no metafields.
        if (tool.types) props['_tool_type'] = tool.types;
        items.push({ id: parseInt(tool.variantId, 10), quantity: 1, properties: props });
      });

      return items;
    }

    async function handleAdd() {
      const reads = slots.map(readSlot);
      const selections = reads.reduce(function (all, read) {
        return all.concat(read.selections);
      }, []);
      const complete = reads.every(function (read) {
        return !read.required || read.complete;
      });
      if (!complete) return;

      const qty = parseInt(qtyInput.value, 10) || minKits;
      const items = buildCartItems(selections, qty, readTools());

      // Guard: nothing real selected (e.g. "Nej tak" on every step).
      if (!items.length) {
        feedback.textContent = 'Vælg mindst én del til dit sæt.';
        feedback.setAttribute('data-state', 'error');
        return;
      }

      addBtn.disabled = true;
      const originalLabel = addLabel.textContent;
      addLabel.textContent = 'Lægger i kurv …';
      feedback.textContent = '';
      feedback.removeAttribute('data-state');

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const data = await resp.json();

        if (!resp.ok) {
          const msg = (data && (data.description || data.message)) || 'Noget gik galt. Prøv igen.';
          throw new Error(msg);
        }

        feedback.textContent = 'Lagt i kurv ✓';
        feedback.setAttribute('data-state', 'success');

        // Notify theme so cart drawer / icon updates.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('product:added'));

        refreshCartTypes();

        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      } catch (err) {
        console.error(err);
        feedback.textContent = err.message || 'Noget gik galt. Prøv igen.';
        feedback.setAttribute('data-state', 'error');
      } finally {
        addLabel.textContent = originalLabel;
        render();
      }
    }

    slots.forEach(function (slot) {
      if (slot.dataset.slotMode === 'pool') initPool(slot);
    });
    initTools();
    if (toolEls.length) refreshCartTypes();
    initPreset();
    initShare();

    // A shared or advertised link should land on a finished build.
    try {
      const incoming = new URLSearchParams(window.location.search);
      if (incoming.get('kit') || incoming.get('v')) {
        const missing = applyConfig(incoming.get('kit'), incoming.get('s'), incoming.get('v'));
        if (missing.length && feedback) {
          const unique = missing.filter(function (v, i, a) { return a.indexOf(v) === i; });
          feedback.textContent = 'Nogle dele fra linket er ikke længere på lager: ' + unique.join(', ') + '.';
          feedback.setAttribute('data-state', 'error');
        }
      }
    } catch (e) { /* a malformed link must never break the builder */ }

    form.addEventListener('change', e => {
      if (e.target.matches('input[type="radio"]')) render();
    });
    qtyInput.addEventListener('input', render);
    qtyInput.addEventListener('change', render);
    qtyInc.addEventListener('click', () => {
      qtyInput.value = (parseInt(qtyInput.value, 10) || minKits) + 1;
      render();
    });
    qtyDec.addEventListener('click', () => {
      qtyInput.value = Math.max(minKits, (parseInt(qtyInput.value, 10) || minKits) - 1);
      render();
    });
    addBtn.addEventListener('click', handleAdd);

    render();
  }

  function init() {
    document.querySelectorAll('[data-kit-builder]').forEach(initKitBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', e => {
    const root = e.target.querySelector('[data-kit-builder]');
    if (root) initKitBuilder(root);
  });
})();
